/**
 * Zone Heatmap Component
 *
 * Renders a continuous density heatmap showing spot occupancy over time.
 * Uses radial gradients with additive blending for smooth visualization.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import {
  HEATMAP_BASE_OPACITY,
  HEATMAP_GRADIENT_RADIUS_MULTIPLIER,
} from "../heatmapConfig";
import { SpotOccupancyData } from "../hooks/useHeatmapData";

interface ZoneHeatmapProps {
  /** Ramp shapes including spots */
  shapes: Shape[];
  /** Occupancy data for each spot */
  occupancyData: Map<string, SpotOccupancyData>;
  /** Canvas width in pixels */
  width: number;
  /** Canvas height in pixels */
  height: number;
  /** Conversion ratio: pixels per foot */
  pixelsToFeetRatio: number;
  /** Whether heatmap is visible */
  visible: boolean;
}

interface SpotCentroid {
  spotId: string;
  x: number; // pixels (centroid)
  y: number; // pixels (centroid)
  radius: number; // pixels
}

/**
 * Calculate centroid and radius for a spot polygon
 */
function calculateSpotCentroid(
  points: Array<{ x: number; y: number }>,
  pixelsToFeetRatio: number
): { x: number; y: number; radius: number } {
  if (!points || points.length === 0) {
    return { x: 0, y: 0, radius: 0 };
  }

  // Calculate centroid
  let sumX = 0;
  let sumY = 0;
  for (const point of points) {
    sumX += point.x;
    sumY += point.y;
  }
  const centroidX = (sumX / points.length) * pixelsToFeetRatio;
  const centroidY = (sumY / points.length) * pixelsToFeetRatio;

  // Calculate diagonal (max distance from centroid) for radius
  let maxDistance = 0;
  for (const point of points) {
    const px = point.x * pixelsToFeetRatio;
    const py = point.y * pixelsToFeetRatio;
    const distance = Math.sqrt(
      Math.pow(px - centroidX, 2) + Math.pow(py - centroidY, 2)
    );
    maxDistance = Math.max(maxDistance, distance);
  }

  const radius = maxDistance * HEATMAP_GRADIENT_RADIUS_MULTIPLIER;

  return { x: centroidX, y: centroidY, radius };
}

/**
 * Get color for occupancy percentage using smooth gradient interpolation
 * Returns RGBA color string
 *
 * Color scale: Green (0%) → Yellow (50%) → Red (100%)
 */
function getColorForOccupancy(occupancyPercent: number, alpha: number): string {
  // Clamp to 0-100 range
  const percent = Math.max(0, Math.min(100, occupancyPercent));

  if (percent < 50) {
    // Green → Yellow (0% to 50%)
    // Green: rgb(76, 175, 80)
    // Yellow: rgb(255, 235, 59)
    const t = percent / 50; // 0 to 1
    const r = Math.round(76 + (255 - 76) * t);
    const g = Math.round(175 + (235 - 175) * t);
    const b = Math.round(80 + (59 - 80) * t);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    // Yellow → Red (50% to 100%)
    // Yellow: rgb(255, 235, 59)
    // Red: rgb(244, 67, 54)
    const t = (percent - 50) / 50; // 0 to 1
    const r = Math.round(255 + (244 - 255) * t);
    const g = Math.round(235 + (67 - 235) * t);
    const b = Math.round(59 + (54 - 59) * t);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}

/**
 * Zone Heatmap Canvas Component
 */
export const ZoneHeatmap: React.FC<ZoneHeatmapProps> = ({
  shapes,
  occupancyData,
  width,
  height,
  pixelsToFeetRatio,
  visible,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Pre-compute spot centroids (memoized)
  const spotCentroids = useMemo((): SpotCentroid[] => {
    const spotShapes = shapes.filter((s) => s.type === "Spot");
    const centroids: SpotCentroid[] = [];

    for (const shape of spotShapes) {
      if (!shape.points || shape.points.length === 0) continue;

      const { x, y, radius } = calculateSpotCentroid(
        shape.points,
        pixelsToFeetRatio
      );

      centroids.push({
        spotId: shape.id,
        x,
        y,
        radius,
      });
    }

    return centroids;
  }, [shapes, pixelsToFeetRatio]);

  // Draw heatmap
  const drawHeatmap = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (!visible) {
      return;
    }

    // Use additive blending for overlapping gradients
    ctx.globalCompositeOperation = "lighter";

    // Draw radial gradient for ALL spots (including 0% occupancy as baseline green)
    for (const centroid of spotCentroids) {
      // Get occupancy data, default to 0% if not found
      const occupancy = occupancyData.get(centroid.spotId);
      const occupancyPercent = occupancy?.occupancyPercent ?? 0;

      const { x, y, radius } = centroid;

      // Create radial gradient
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

      const color = getColorForOccupancy(
        occupancyPercent,
        HEATMAP_BASE_OPACITY
      );
      const transparentColor = getColorForOccupancy(
        occupancyPercent,
        0
      );

      gradient.addColorStop(0, color);
      gradient.addColorStop(1, transparentColor);

      // Draw filled circle with gradient
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Reset composite operation
    ctx.globalCompositeOperation = "source-over";
  }, [visible, occupancyData, spotCentroids, width, height]);

  // Redraw when dependencies change
  useEffect(() => {
    drawHeatmap();
  }, [drawHeatmap]);

  if (!visible) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none", // Pass-through - don't block interactions with aircraft
        zIndex: 1.5, // Between ramp image (0) and aircraft (higher)
      }}
    />
  );
};
